'use client';

import { useCallback } from 'react';
import type { Editor } from '@tiptap/react';

// --- Icons ---
import { CodeBlockIcon } from 'containers/Tiptap/components/tiptap-icons/code-block-icon';
import { HeadingOneIcon } from 'containers/Tiptap/components/tiptap-icons/heading-one-icon';
import { HeadingTwoIcon } from 'containers/Tiptap/components/tiptap-icons/heading-two-icon';
import { HeadingThreeIcon } from 'containers/Tiptap/components/tiptap-icons/heading-three-icon';
import { ImageIcon } from 'containers/Tiptap/components/tiptap-icons/image-icon';
import { ListIcon } from 'containers/Tiptap/components/tiptap-icons/list-icon';
import { ListOrderedIcon } from 'containers/Tiptap/components/tiptap-icons/list-ordered-icon';
import { BlockquoteIcon } from 'containers/Tiptap/components/tiptap-icons/blockquote-icon';
import { ListTodoIcon } from 'containers/Tiptap/components/tiptap-icons/list-todo-icon';
import { MinusIcon } from 'containers/Tiptap/components/tiptap-icons/minus-icon';
import { TypeIcon } from 'containers/Tiptap/components/tiptap-icons/type-icon';
import { SmilePlusIcon } from 'containers/Tiptap/components/tiptap-icons/smile-plus-icon';
import { TableIcon } from 'containers/Tiptap/components/tiptap-icons/table-icon';
import { ListIndentedIcon } from 'containers/Tiptap/components/tiptap-icons/list-indented-icon';
import { GridIcon } from 'containers/Tiptap/components/tiptap-icons/grid-icon';
import { MathIcon } from 'containers/Tiptap/components/tiptap-icons/math-icon';

// --- Lib ---
import { isExtensionAvailable, isNodeInSchema } from 'containers/Tiptap/lib/tiptap-utils';

// --- Tiptap UI ---
import type { SuggestionItem } from 'containers/Tiptap/components/tiptap-ui-utils/suggestion-menu';
import { addEmojiTrigger } from 'containers/Tiptap/components/tiptap-ui/emoji-trigger-button';

// --- Constants ---
import { RISK_MATRIX_CONSTANTS } from 'containers/Tiptap/components/tiptap-node/risk-assessments-node/constants';
import {
  DEFAULT_COLUMNS as RISK_ASSESSMENT_DEFAULT_COLUMNS,
  RISK_ASSESSMENT_DEFAULTS,
} from 'containers/Tiptap/components/tiptap-node/risk-assessment-node/constants';

export interface SlashMenuConfig {
  enabledItems?: SlashMenuItemType[];
  customItems?: SuggestionItem[];
  itemGroups?: {
    [key in SlashMenuItemType]?: string;
  };
  showGroups?: boolean;
}

const texts = {
  // Style
  text: {
    title: 'Text',
    subtext: 'Regular text paragraph',
    keywords: ['p', 'paragraph', 'text'],
    badge: TypeIcon,
    group: 'Style',
  },
  heading_1: {
    title: 'Heading 1',
    subtext: 'Top-level heading',
    keywords: ['h', 'heading1', 'h1'],
    badge: HeadingOneIcon,
    group: 'Style',
  },
  heading_2: {
    title: 'Heading 2',
    subtext: 'Key section heading',
    keywords: ['h2', 'heading2', 'subheading'],
    badge: HeadingTwoIcon,
    group: 'Style',
  },
  heading_3: {
    title: 'Heading 3',
    subtext: 'Subsection and group heading',
    keywords: ['h3', 'heading3', 'subheading'],
    badge: HeadingThreeIcon,
    group: 'Style',
  },
  bullet_list: {
    title: 'Bullet List',
    subtext: 'List with unordered items',
    keywords: ['ul', 'li', 'list', 'bulletlist', 'bullet list'],
    badge: ListIcon,
    group: 'Style',
  },
  ordered_list: {
    title: 'Numbered List',
    subtext: 'List with ordered items',
    keywords: ['ol', 'li', 'list', 'numberedlist', 'numbered list'],
    badge: ListOrderedIcon,
    group: 'Style',
  },
  task_list: {
    title: 'To-do list',
    subtext: 'List with tasks',
    keywords: ['tasklist', 'task list', 'todo', 'checklist'],
    badge: ListTodoIcon,
    group: 'Style',
  },
  quote: {
    title: 'Blockquote',
    subtext: 'Blockquote block',
    keywords: ['quote', 'blockquote'],
    badge: BlockquoteIcon,
    group: 'Style',
  },
  code_block: {
    title: 'Code Block',
    subtext: 'Code block with syntax highlighting',
    keywords: ['code', 'pre'],
    badge: CodeBlockIcon,
    group: 'Style',
  },

  // Insert
  emoji: {
    title: 'Emoji',
    subtext: 'Insert an emoji',
    keywords: ['emoji', 'emoticon', 'smiley'],
    badge: SmilePlusIcon,
    group: 'Insert',
  },
  table: {
    title: 'Table',
    subtext: 'Insert a table',
    aliases: ['table', 'insertTable'],
    badge: TableIcon,
    group: 'Insert',
  },
  divider: {
    title: 'Separator',
    subtext: 'Horizontal line to separate content',
    keywords: ['hr', 'horizontalRule', 'line', 'separator'],
    badge: MinusIcon,
    group: 'Insert',
  },
  toc: {
    title: 'Table of contents',
    subtext: 'Insert a table of contents',
    keywords: ['toc', 'tableofcontents', 'table of contents'],
    badge: ListIndentedIcon,
    group: 'Insert',
  },
  risk_matrix: {
    title: 'Risk Matrix',
    subtext: 'Insert a risk assessment matrix',
    keywords: ['risk', 'matrix', 'assessment', 'risk matrix', 'risk assessment'],
    badge: GridIcon,
    group: 'Insert',
  },
  risk_assessment: {
    title: 'Risk Assessment',
    subtext: 'Insert a risk assessment table',
    keywords: ['risk', 'assessment', 'hazard', 'risk assessment', 'hazards', 'risk register'],
    badge: TableIcon,
    group: 'Insert',
  },

  // Upload
  image: {
    title: 'Attachment',
    subtext: 'Upload a file or image',
    keywords: ['attachment', 'file', 'image', 'imageUpload', 'upload', 'img', 'picture', 'media', 'url'],
    badge: ImageIcon,
    group: 'Upload',
  },

  // Advanced
  math: {
    title: 'Math Equation',
    subtext: 'Insert a LaTeX math equation',
    keywords: ['math', 'latex', 'equation', 'formula', 'katex'],
    badge: MathIcon,
    group: 'Advanced',
  },
};

export type SlashMenuItemType = keyof typeof texts;

// Canonical list of every slash-menu item. Import this rather than re-listing the items
// elsewhere, so a new item added to `texts` is picked up automatically.
export const ALL_SLASH_MENU_ITEMS = Object.keys(texts) as SlashMenuItemType[];

interface SlashMenuItemImplementation {
  check: (editor: Editor) => boolean;
  action: (params: { editor: Editor }) => void;
}

const getItemImplementations = (): Record<SlashMenuItemType, SlashMenuItemImplementation> => ({
  // Style
  text: {
    check: (editor: Editor) => isNodeInSchema('paragraph', editor),
    action: ({ editor }: { editor: Editor }) => {
      editor.chain().focus().setParagraph().run();
    },
  },
  heading_1: {
    check: (editor: Editor) => isNodeInSchema('heading', editor),
    action: ({ editor }: { editor: Editor }) => {
      editor.chain().focus().toggleHeading({ level: 1 }).run();
    },
  },
  heading_2: {
    check: (editor: Editor) => isNodeInSchema('heading', editor),
    action: ({ editor }: { editor: Editor }) => {
      editor.chain().focus().toggleHeading({ level: 2 }).run();
    },
  },
  heading_3: {
    check: (editor: Editor) => isNodeInSchema('heading', editor),
    action: ({ editor }: { editor: Editor }) => {
      editor.chain().focus().toggleHeading({ level: 3 }).run();
    },
  },
  bullet_list: {
    check: (editor: Editor) => isNodeInSchema('bulletList', editor),
    action: ({ editor }: { editor: Editor }) => {
      editor.chain().focus().toggleBulletList().run();
    },
  },
  ordered_list: {
    check: (editor: Editor) => isNodeInSchema('orderedList', editor),
    action: ({ editor }: { editor: Editor }) => {
      editor.chain().focus().toggleOrderedList().run();
    },
  },
  task_list: {
    check: (editor: Editor) => isNodeInSchema('taskList', editor),
    action: ({ editor }: { editor: Editor }) => {
      editor.chain().focus().toggleTaskList().run();
    },
  },
  quote: {
    check: (editor: Editor) => isNodeInSchema('blockquote', editor),
    action: ({ editor }: { editor: Editor }) => {
      editor.chain().focus().toggleBlockquote().run();
    },
  },
  code_block: {
    check: (editor: Editor) => isNodeInSchema('codeBlock', editor),
    action: ({ editor }: { editor: Editor }) => {
      editor.chain().focus().toggleNode('codeBlock', 'paragraph').run();
    },
  },

  // Insert
  emoji: {
    check: (editor: Editor) => isExtensionAvailable(editor, ['emoji', 'emojiPicker']),
    action: ({ editor }: { editor: Editor }) => addEmojiTrigger(editor),
  },
  divider: {
    check: (editor: Editor) => isNodeInSchema('horizontalRule', editor),
    action: ({ editor }: { editor: Editor }) => {
      editor.chain().focus().setHorizontalRule().run();
    },
  },
  toc: {
    check: (editor: Editor) => isNodeInSchema('tocNode', editor),
    action: ({ editor }: { editor: Editor }) => {
      editor.chain().focus().insertTocNode().run();
    },
  },
  risk_matrix: {
    check: (editor: Editor) => isNodeInSchema('riskMatrix', editor),
    action: ({ editor }: { editor: Editor }) => {
      editor
        .chain()
        .focus()
        .setRiskMatrixNode({
          defaultRows: RISK_MATRIX_CONSTANTS.DEFAULT_ROWS,
          defaultColumns: RISK_MATRIX_CONSTANTS.DEFAULT_COLUMNS,
          defaultImpactLabels: RISK_MATRIX_CONSTANTS.DEFAULT_IMPACT_LABELS,
          defaultLikelihoodLabels: RISK_MATRIX_CONSTANTS.DEFAULT_LIKELIHOOD_LABELS,
          editable: true,
        })
        .run();
    },
  },
  risk_assessment: {
    check: (editor: Editor) => isNodeInSchema('riskAssessment', editor),
    action: ({ editor }: { editor: Editor }) => {
      editor
        .chain()
        .focus()
        .setRiskAssessmentNode({
          defaultColumns: RISK_ASSESSMENT_DEFAULT_COLUMNS,
          defaultLikelihoodLabels: RISK_ASSESSMENT_DEFAULTS.LIKELIHOOD_LABELS,
          defaultSeverityLabels: RISK_ASSESSMENT_DEFAULTS.SEVERITY_LABELS,
          editable: true,
        })
        .run();
    },
  },
  table: {
    check: (editor: Editor) => isNodeInSchema('table', editor),
    action: ({ editor }: { editor: Editor }) => {
      editor
        .chain()
        .focus()
        .insertTable({
          rows: 3,
          cols: 3,
          withHeaderRow: false,
        })
        .run();
    },
  },

  // Upload
  image: {
    check: (editor: Editor) => isNodeInSchema('image', editor),
    action: ({ editor }: { editor: Editor }) => {
      editor
        .chain()
        .focus()
        .insertContent({
          type: 'imageUpload',
        })
        .run();
    },
  },

  // Advanced
  math: {
    check: (editor: Editor) => isExtensionAvailable(editor, ['mathematics']),
    action: ({ editor }: { editor: Editor }) => {
      editor.chain().focus().insertContent('$E = mc^2$').run();
    },
  },
});

interface UseSlashDropdownMenuReturn {
  getSlashMenuItems: (editor: Editor) => SuggestionItem[];
  config: SlashMenuConfig | undefined;
}

function organizeItemsByGroups(items: SuggestionItem[], showGroups: boolean): SuggestionItem[] {
  if (!showGroups) {
    return items.map((item) => ({ ...item, group: '' }));
  }

  const groups: { [groupLabel: string]: SuggestionItem[] } = {};

  // Group items
  items.forEach((item) => {
    const groupLabel = item.group || '';
    if (!groups[groupLabel]) {
      groups[groupLabel] = [];
    }
    groups[groupLabel].push(item);
  });

  // Flatten groups in order (this maintains the visual order for keyboard navigation)
  const organizedItems: SuggestionItem[] = [];
  Object.entries(groups).forEach(([, groupItems]) => {
    organizedItems.push(...groupItems);
  });

  return organizedItems;
}

/**
 * Custom hook for slash dropdown menu functionality
 */
export function useSlashDropdownMenu(config?: SlashMenuConfig): UseSlashDropdownMenuReturn {
  const getSlashMenuItems = useCallback(
    (editor: Editor) => {
      const items: SuggestionItem[] = [];

      const enabledItems = config?.enabledItems || (Object.keys(texts) as SlashMenuItemType[]);
      const showGroups = config?.showGroups !== false;

      const itemImplementations = getItemImplementations();

      enabledItems.forEach((itemType) => {
        const itemImpl = itemImplementations[itemType];
        const itemText = texts[itemType];

        if (itemImpl && itemText && itemImpl.check(editor)) {
          const item: SuggestionItem = {
            onSelect: ({ editor: selectEditor }) => itemImpl.action({ editor: selectEditor }),
            ...itemText,
          };

          if (config?.itemGroups?.[itemType]) {
            item.group = config.itemGroups[itemType];
          } else if (!showGroups) {
            item.group = '';
          }

          items.push(item);
        }
      });

      if (config?.customItems) {
        items.push(...config.customItems);
      }

      // Reorganize items by groups to ensure keyboard navigation works correctly
      return organizeItemsByGroups(items, showGroups);
    },
    [config]
  );

  return {
    getSlashMenuItems,
    config,
  };
}
